import { Stack } from '@mui/material';
import { GetInvoiceProductsIntegrationEntitiesResponse } from '@vy/api-definition';
import { INTEGRATION_PROVIDERS, NetSuiteProductEntity } from '@vy/types';
import { isNullOrUndefined, ItemOf } from '@vy/utils';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { VyDialog } from '../../../../../../../../../../../../components/new-design/feedback/VyDialog';
import { useUpdateProductIntegrationEntities } from '../../../../../../../../../../../../hooks/requests/use-update-product-integration-entites';
import { useIntegrationEntities } from '../../../../../../../../../../../../hooks/use-integration-entities';
import { runMutation } from '../../../../../../../../../../../../utils/vy-mutation';
import { NetSuiteClassesDropdown } from '../../../../../../../../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteClassesDropdown';
import { NetSuiteProductsDropdown } from '../../../../../../../../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteProductsDropdown';
import {
  ProductErpForm,
  ProductErpFormSchema,
} from '../erp-product-settings-accordion-types/product-erp-form-schema';
import {
  buildUpdateLineItemIntegrationEntitiesPayload,
  getProductErpFormDefaultValues,
  mapFormValuesToIntegrationEntity,
} from '../erp-product-settings-accordion-utils/erp-product-settings-accordion-form-utils';

type EditErpProductSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
  product: ItemOf<GetInvoiceProductsIntegrationEntitiesResponse['products']>;
  invoiceId: string;
  customerId: string;
};

export const EditErpProductSettingsDialog: FC<EditErpProductSettingsDialogProps> = ({
  open,
  onClose,
  product,
  invoiceId,
  customerId,
}) => {
  const {
    integrationEntityIndex: netSuiteIntegrationEntityIndex,
    integrationEntities: productIntegrationEntities,
  } = useIntegrationEntities<NetSuiteProductEntity>({
    integrationEntities: product.integrationEntities,
    integrationProvider: INTEGRATION_PROVIDERS.netSuite,
  });
  const { control, handleSubmit, reset, setValue } = useForm<ProductErpForm>({
    defaultValues:
      getProductErpFormDefaultValues(productIntegrationEntities) ?? ProductErpFormSchema.parse({}),
  });

  const updateProductMutation = useUpdateProductIntegrationEntities(product.lineItemId, invoiceId);

  useEffect(() => {
    if (open) {
      const resetFormValues = getProductErpFormDefaultValues(productIntegrationEntities);
      reset(resetFormValues ?? ProductErpFormSchema.parse({}));
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    reset();
  };

  const submitForm = handleSubmit(async (formValues) => {
    const netSuiteIntegrationEntity = mapFormValuesToIntegrationEntity(formValues);

    const updatedIntegrationEntities = buildUpdateLineItemIntegrationEntitiesPayload(
      productIntegrationEntities,
      netSuiteIntegrationEntityIndex,
      netSuiteIntegrationEntity,
    );

    await runMutation(
      updateProductMutation,
      {
        integrationEntities: updatedIntegrationEntities,
      },
      {
        pending: 'Updating product...',
        success: 'Product updated successfully',
        error: 'Failed to update product',
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  });

  return (
    <VyDialog
      label={product.name}
      labelDescription="Edit ERP settings"
      open={open}
      onClose={handleClose}
      ctaText="Confirm"
      onSubmit={submitForm}
      cancelButtonText="Cancel"
      disableDividers
    >
      <Stack sx={{
        gap: 1
      }}>
        <NetSuiteProductsDropdown
          fieldPath="product"
          control={control}
          showDropdown
          customerId={customerId}
          onChange={(modifiedIntegrationEntity) => {
            if (isNullOrUndefined(modifiedIntegrationEntity)) {
              setValue('classMetadata', null);
            }
          }}
        />
        <NetSuiteClassesDropdown control={control} fieldPath="classMetadata" />
      </Stack>
    </VyDialog>
  );
};
